import { useCallback, useEffect, useRef, useState } from 'react';
import { Overlay } from '../ui/Overlay';
import '../styles/onboarding.css';

/* ---------------------------------------------------------------------------
   Onboarding — the concierge's walk-through of the house (Jonathan email
   2026-10-07: "an onboarding UI in the style of JitsLab, IE walk through
   features, but in the concierge tone of voice … exhaustive even if feature
   is coming soon").

   Structural model is JitsLab web-v2/src/screens/Onboarding: a fullscreen
   step wizard with feature cards, progress, Back/Next/Skip. What changes is
   the voice — this is the wry butler showing a couple around — and the
   coverage: every area of Pear gets a step, and what is not built yet is
   named plainly on the last step instead of being hidden behind tiles that
   go nowhere (the anti-pattern card LJEuXb38LG7T removed from the panels).

   Portalled to document.body: #chat-screen ends .fade-in on a transform,
   which would otherwise become the containing block for position: fixed.
   --------------------------------------------------------------------------- */

/** localStorage flag: the tour has been seen, do not auto-open it again. */
export const ONBOARDED_KEY = 'pear.onboarded';

interface Step {
  id: string;
  /** Eyebrow: where this lives in the app. */
  place: string;
  title: string;
  /** The concierge speaking. */
  body: string;
  /** Everything in this area — exhaustive, short lines. */
  items: string[];
  /** Optional action this step offers — e.g. the interests quiz (card
   *  mMl44G43YK5X): the tour points at the thing and lets you go do it,
   *  instead of describing it and hoping you find it later. */
  cta?: { label: string; hash: string };
}

/** Local clock for the opening line (Jonathan email 2026-10-07: "should note
 *  local time of day to start with eg Good evening, I'm Pear."). Three bands —
 *  morning, afternoon, evening — computed on render, never stored: the tour is
 *  a live screen, so a device opened at 19:00 reads "Good evening". */
function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export const STEPS: Step[] = [
  {
    id: 'welcome',
    place: 'Welcome',
    title: 'Allow me to show you around.',
    body:
      'I am your faithful concierge. I keep the house in order so the two of you can enjoy it — ' +
      'and I will point at everything on the way through. Nine short stops, then the place is yours.',
    items: [
      'Thread, Us, Life and Notebook sit in the bar at the bottom',
      'More opens the full menu — every screen and sub-view',
      'Anything you would rather not show me, you switch off in Settings',
    ],
  },
  {
    id: 'thread',
    place: 'Thread',
    title: 'Thread is home base.',
    body:
      'Everything typed here belongs to both of you — one stream, two people, no folders to argue about.',
    items: [
      'Write, and it lands for your partner the moment you send it',
      'The + menu holds everything you can create: a message, an ask, a question, kudos — plus a note, an idea, a memory, a date idea and a link',
      'Offer kudos as a show of gratitude',
      'Weekly sync is in More — the concierge gathers the week for your Plan check-in',
      'Save any message to the Notebook — you pick me or us every time',
      'Filter to chat only, or show everything including the cards',
    ],
  },
  {
    id: 'us',
    place: 'Us',
    title: 'Us — the two of you, on purpose.',
    body: 'Quality time, even if you don\u2019t have much time.',
    items: [
      'Questions: one a day, answered separately, revealed together',
      'Reveal history: every past answer, in order, for when nostalgia strikes',
      'Game: I run a back-and-forth round — places, restaurants, hypotheticals',
      'Date ideas: I draft them from your interests, you both veto — the list learns',
      'Digest: a weekly write-up of your headlines, once you grant the permission',
    ],
  },
  {
    id: 'life',
    place: 'Life',
    title: 'Life — the business of living together.',
    body: 'Where requests and household tasks go so they stop living in your heads.',
    items: [
      'Requests: ask for something, your partner accepts, counters or declines',
      'Today holds what needs you and what is coming up, in one list',
      'Household: assign, set it recurring, tick it off — kudos included',
      'Household items can sync to your Google Calendar and Tasks once you connect Google',
      'Anything recurring shows up in the Thread as it comes due',
    ],
  },
  {
    id: 'notebook',
    place: 'Notebook',
    title: 'Notebook — what you both meant to keep.',
    body: 'Capture anything meaningful, memorable or the next movie night.',
    items: [
      'Notebook: Everything, Collection, Gifts, Memories, Dates, Notes, Ideas',
      'Share a link and I will organise for your next listening or viewing session, and save the article you want to discuss over brunch',
      'Choose me for private, us for shared; mine never reaches your partner',
      "I'm at your disposal to find the perfect gift — provide initial direction and I'll take it from there",
      'I work quietly in the background and surface what you need upon your request',
    ],
  },
  {
    id: 'concierge',
    place: 'Settings · Concierge',
    title: 'I read nothing until you say so.',
    body:
      'My permissions live in Settings, under Concierge. Everything starts off, ' +
      'and every read I make is written in a log you can read back.',
    items: [
      'Chat, Inbox, Questions, Vault, To-dos, Interests, Partner digest — seven switches',
      'Off means off: no reading, no writing, no drafting',
      'The read log shows what I looked at and when',
      'Your interests profile is how I word the topics I bring you',
    ],
    /* Card mMl44G43YK5X: the one step that hands you the thing itself. The
       quiz is four questions and the concierge reads better with it done, so
       this step offers the trip rather than just naming the room. */
    cta: { label: 'Set your interests — 2 min', hash: '#settings-you' },
  },
  {
    id: 'settings',
    place: 'Settings · More',
    title: 'The rest of the house.',
    body:
      'Most of the rest is one tap from More, in the bottom bar — and each of those ' +
      'screens has its own sections inside it.',
    items: [
      'Settings: You, Your interests, Us, Concierge, Leaving, Build',
      'Set the dates that matter: birthdays, your anniversary, the occasions you want remembered',
      'Decide how involved I am — what I may read, and how much I act on my own',
      'If allowed the opportunity, I will prepare a digest of your respective interests to enrich your daily conversations',
    ],
  },
  {
    id: 'soon',
    place: 'On the way',
    title: 'Still being fitted out.',
    body: 'I would rather name these than pretend the room is finished.',
    items: [
      'Budget — spending together, still on the drawing board',
      'Photo memories — a picture attached to the memory card',
      'Long-distance mode — the same screens across time zones',
    ],
  },
  {
    id: 'done',
    place: 'That is the tour',
    title: 'The place is yours.',
    body:
      'Start in Thread — say something, ask me something, or save the link you both ' +
      'keep meaning to watch. I will be here, reading nothing I am not allowed to.',
    items: [
      'Tap Begin, or come back any time from More → Take the tour, or from Help',
    ],
  },
];

interface Props {
  onClose: () => void;
}

export function Onboarding({ onClose }: Props) {
  const [index, setIndex] = useState(0);

  const step = STEPS[index];
  const last = index === STEPS.length - 1;
  // The welcome line carries the local clock; every other step keeps its own title.
  const title = step.id === 'welcome' ? `${greeting()}, I'm Pear.` : step.title;

  /* Focus follows the reading (sol review M7): each step change lands focus on
     the step's own heading, so a screen-reader or keyboard user hears where
     they are instead of watching focus churn on the Skip button. */
  useEffect(() => {
    document.getElementById('onb-title')?.focus();
  }, [index]);

  const finish = useCallback(() => {
    try {
      localStorage.setItem(ONBOARDED_KEY, '1');
    } catch {
      /* private mode — the tour will simply offer itself again */
    }
    onClose();
  }, [onClose]);

  /* The tour's own keyboard contract, on top of the shared Overlay (which owns
     Escape, the focus trap and the inert background): ← / → move between the
     steps, the same direction as the swipe. It only runs while the tour
     actually holds the slot — when another overlay (the pairing ceremony) is
     showing, the tour renders no panel at all, and keys pressed then belong
     to the surface the user can see, not to a screen behind it. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!document.getElementById('onboarding-panel')) return;
      if (e.key === 'ArrowRight' && !last) setIndex(i => i + 1);
      if (e.key === 'ArrowLeft' && index > 0) setIndex(i => i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, last]);

  // Touch swipe (Jonathan email 2026-10-07: "onboarding should have swipe back
  // and forth on mobile"). Horizontal only — a vertical drag is the reader
  // scrolling the card, and it must never steal that.
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0 && !last) setIndex(i => i + 1);
    else if (dx > 0 && index > 0) setIndex(i => i - 1);
  };

  return (
    <Overlay
      open
      onClose={finish}
      className="onb-overlay"
      panelClassName="onb-panel"
      panelId="onboarding-panel"
      label="Take the tour"
      testId="onboarding"
      initialFocus="#onb-title"
      panelProps={{ onTouchStart, onTouchEnd }}
    >
      <div className="onb-top">
        <span className="onb-place" id="onb-place">{step.place}</span>
        <button type="button" className="onb-close" id="btn-onboarding-close" onClick={finish}>
          Skip
        </button>
      </div>

      {/* Step changes announced to assistive tech (sol review M7): outside the
          keyed body so the node survives every step and polite readers get the
          new position without re-reading the whole card. */}
      <p className="onb-live" id="onb-live" aria-live="polite">
        {`Step ${index + 1} of ${STEPS.length} — ${step.place}`}
      </p>

      <div className="onb-body" key={step.id} data-step={step.id}>
        <h2 className="onb-title" id="onb-title" tabIndex={-1}>{title}</h2>
        <p className="onb-copy">{step.body}</p>
        <ul className="onb-items" id="onb-items">
          {step.items.map(item => (
            <li className="onb-item" key={item}>
              <span className="onb-dot" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
        {/* Card mMl44G43YK5X: the interests step's own door. Finishing the
            tour first means the person comes back to a house they know, with
            the quiz waiting on the You section. */}
        {step.cta && (
          <button
            type="button"
            className="onb-btn onb-btn-primary onb-cta"
            id="onb-cta"
            data-hash={step.cta.hash}
            onClick={() => {
              finish();
              window.location.hash = step.cta!.hash;
            }}
          >
            {step.cta.label}
          </button>
        )}
      </div>

      <div className="onb-foot">
        <div
          className="onb-progress"
          role="progressbar"
          aria-label="Tour progress"
          aria-valuemin={1}
          aria-valuemax={STEPS.length}
          aria-valuenow={index + 1}
          aria-valuetext={`Step ${index + 1} of ${STEPS.length}`}
        >
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={`onb-pip${i === index ? ' is-current' : ''}${i < index ? ' is-done' : ''}`}
            />
          ))}
        </div>
        <div className="onb-actions">
          <button
            type="button"
            className="onb-btn onb-btn-ghost"
            id="btn-onboarding-back"
            disabled={index === 0}
            onClick={() => setIndex(i => Math.max(0, i - 1))}
          >
            Back
          </button>
          <button
            type="button"
            className="onb-btn onb-btn-primary"
            id="btn-onboarding-next"
            onClick={() => (last ? finish() : setIndex(i => i + 1))}
          >
            {last ? 'Begin' : 'Next'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
